import type { FC } from "react";
import { twMerge } from "tailwind-merge";
import {
  formatFunnelPreciseShare,
  formatFunnelShare,
} from "./funnelFormatting.js";

export interface FunnelMomentShareBarProps {
  momentName: string;
  /** Of the stage's interactions, those with the moment. */
  share: number;
  /** Of those, the ones that dropped off; null in the last stage. */
  dropOffRate: number | null;
  /** The stage's interactions in prose, from `formatFunnelStageInteractions`. */
  stageInteractions: string;
  /** The slideover's full-width bar, without the trailing percentage. */
  isExpanded?: boolean;
}

/** The moment's share of the stage, split into dropped off (orange) and continued (indigo). */
export const FunnelMomentShareBar: FC<FunnelMomentShareBarProps> = (props) => {
  const {
    momentName,
    share,
    dropOffRate,
    stageInteractions,
    isExpanded = false,
  } = props;
  const description =
    dropOffRate === null
      ? `${formatFunnelShare(share)} of ${stageInteractions} had this moment.`
      : `${formatFunnelShare(share)} of ${stageInteractions} had this moment: ${formatFunnelPreciseShare(share * dropOffRate)} dropped off afterward and ${formatFunnelPreciseShare(share * (1 - dropOffRate))} continued.`;

  return (
    <span
      className={twMerge(
        "flex shrink-0 items-center gap-3",
        isExpanded ? "w-full" : "w-44 sm:w-64",
      )}
      title={description}
    >
      <span
        role="img"
        aria-label={`${momentName}. ${description}`}
        className={twMerge(
          "flex flex-1 overflow-hidden rounded-full bg-border-strong/40",
          isExpanded ? "h-3" : "h-2",
        )}
      >
        <span
          className="flex h-full overflow-hidden rounded-full"
          style={{ width: `${share * 100}%` }}
        >
          <span
            className="h-full bg-orange-400"
            style={{ width: `${(dropOffRate ?? 0) * 100}%` }}
          />
          <span className="h-full flex-1 bg-indigo-400" />
        </span>
      </span>
      {!isExpanded && (
        <span className="w-9 text-right text-xs text-muted-foreground tabular-nums">
          {formatFunnelShare(share)}
        </span>
      )}
    </span>
  );
};
